import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate, fmtINR, fmtDateTime } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { HealthBadge, StatusBadge } from "./Projects";
import { ChartLine, CheckCircle, Warning, Clock, CurrencyInr, TrendUp } from "@phosphor-icons/react";

const Kpi = ({ label, value, icon: Icon, tone = "" }) => (
  <Card className={`p-4 flex items-center gap-3 ${tone}`}>
    {Icon && <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-orange-600"><Icon size={20} /></div>}
    <div className="min-w-0">
      <div className="text-xs opacity-60 uppercase tracking-wider truncate">{label}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
    </div>
  </Card>
);

export default function ProjectDashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/projects/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="p-10 text-center opacity-60">Loading…</div>;
  const k = data.kpi;

  return (
    <div className="space-y-4">
      <PageHeader title="Project Dashboard"
                  description="Live overview of every project — health, value, cost and profitability." />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Kpi label="Total Projects" value={k.total} icon={ChartLine} />
        <Kpi label="Active" value={k.active} icon={Clock} />
        <Kpi label="Completed" value={k.completed} icon={CheckCircle} />
        <Kpi label="Delayed" value={k.delayed} icon={Warning} />
        <Kpi label="At Risk" value={k.at_risk} icon={Warning} />
        <Kpi label="Total Value" value={fmtINR(k.total_value)} icon={CurrencyInr} />
        <Kpi label="Total Cost" value={fmtINR(k.total_cost)} icon={CurrencyInr} />
        <Kpi label="Pending Billing" value={fmtINR(k.pending_billing)} icon={CurrencyInr} />
        <Kpi label="Outstanding" value={fmtINR(k.outstanding)} icon={CurrencyInr} />
        <Kpi label="Est. Profit" value={fmtINR(k.estimated_profit)} icon={TrendUp} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="text-sm font-medium mb-3">Project Status</div>
          <div className="space-y-2">
            {Object.entries(data.status_distribution).map(([s, c]) => (
              <div key={s} className="flex items-center gap-3">
                <StatusBadge s={s} />
                <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded h-2 overflow-hidden">
                  <div className="bg-orange-500 h-full"
                       style={{ width: `${k.total ? (c / k.total * 100) : 0}%` }} />
                </div>
                <div className="text-xs tabular-nums w-6 text-right">{c}</div>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm font-medium mb-3">Project Health</div>
          <div className="space-y-2">
            {Object.entries(data.health_distribution).map(([h, c]) => (
              <div key={h} className="flex items-center gap-3">
                <HealthBadge h={h} />
                <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded h-2 overflow-hidden">
                  <div className={h === "on_track" ? "bg-green-500 h-full" : h === "at_risk" ? "bg-yellow-500 h-full" : "bg-red-500 h-full"}
                       style={{ width: `${k.total ? (c / k.total * 100) : 0}%` }} />
                </div>
                <div className="text-xs tabular-nums w-6 text-right">{c}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="text-sm font-medium mb-3">Upcoming Deadlines</div>
          {data.upcoming_deadlines.length === 0 && <div className="text-xs opacity-60">No upcoming deadlines.</div>}
          {data.upcoming_deadlines.map((p) => (
            <div key={p.id} className="flex justify-between py-1 border-b last:border-0 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/30 -mx-2 px-2 rounded"
                 onClick={() => navigate(`/projects/${p.id}`)}>
              <span><span className="font-mono text-xs opacity-60">{p.code}</span> {p.name}</span>
              <span className="text-xs opacity-70">{fmtDate(p.end_date)}</span>
            </div>
          ))}
        </Card>
        <Card className="p-4">
          <div className="text-sm font-medium mb-3">Upcoming Milestones</div>
          {data.upcoming_milestones.length === 0 && <div className="text-xs opacity-60">No upcoming milestones.</div>}
          {data.upcoming_milestones.map((m) => (
            <div key={m.id} className="flex justify-between py-1 border-b last:border-0 text-sm">
              <span>{m.name} <span className="text-xs opacity-60">({m.completion_pct}%)</span></span>
              <span className="text-xs opacity-70">{fmtDate(m.target_date)}</span>
            </div>
          ))}
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="text-sm font-medium mb-3">Overdue Tasks</div>
          {data.overdue_tasks.length === 0 && <div className="text-xs opacity-60">No overdue tasks 🎉</div>}
          {data.overdue_tasks.map((t) => (
            <div key={t.id} className="flex justify-between py-1 border-b last:border-0 text-sm">
              <span>{t.name}</span>
              <span className="text-xs text-red-600">{fmtDate(t.due_date)}</span>
            </div>
          ))}
        </Card>
        <Card className="p-4">
          <div className="text-sm font-medium mb-3">Recent Activity</div>
          {data.recent_activity.length === 0 && <div className="text-xs opacity-60">No activity yet.</div>}
          {data.recent_activity.map((a) => (
            <div key={a.id} className="py-1 border-b last:border-0 text-xs">
              <span className="font-medium">{a.actor_name}</span>{" "}
              <span className="opacity-70">{(a.action || "").replace(/_/g, " ")}</span>
              {a.detail && <span className="opacity-70"> — {a.detail}</span>}
              <div className="opacity-50 text-[10px]">{fmtDateTime(a.timestamp)}</div>
            </div>
          ))}
        </Card>
      </div>

      <Card className="p-4">
        <div className="text-sm font-medium mb-3">Revenue vs Cost (Top 10)</div>
        <div className="space-y-2">
          {data.revenue_vs_cost.length === 0 && <div className="text-xs opacity-60">No project data.</div>}
          {data.revenue_vs_cost.map((p) => {
            const max = Math.max(p.revenue, p.cost, 1);
            return (
              <div key={p.code} className="text-xs">
                <div className="flex justify-between mb-1"><span className="font-mono opacity-60">{p.code}</span>
                  <span className={p.profit >= 0 ? "text-green-600" : "text-red-600"}>Profit: {fmtINR(p.profit)}</span>
                </div>
                <div className="flex gap-1 h-3">
                  <div className="bg-green-500" style={{ width: `${(p.revenue / max) * 50}%` }} title={`Revenue ${fmtINR(p.revenue)}`} />
                  <div className="bg-red-400" style={{ width: `${(p.cost / max) * 50}%` }} title={`Cost ${fmtINR(p.cost)}`} />
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
